import React from 'react';
import { View, Text, StyleSheet } from 'react-native';
import { shallowEqual, useSelector } from 'react-redux';
import {
  getNotificationStatusBackgroundColor, getNotificationStatusColor, getNotificationStatusText,
  getNotificationTime,
} from '../../../../../components/NotificationsLog/data';
import Colors from '../../../../../constants/Colors';
import { TicketPriorityTitle, generateAvatarColor } from '../../../data';
import Logo from '../../../../../components/Logo';
import Style from '../../../../../constants/Style';
import { getInitials } from '../../TicketAssigneeIcon';
import { TicketPriority, ticketPriorityIsValid, TicketStatus } from '../../../../../constants/Types';
import { Building } from '../../../../LocationManagement/components/AddBuildingModal';
import { BuildingSettingsPriority } from '../../../../LocationManagement/components/BuildingSettingsModal/data';
import PriorityLabel from '../../../../LocationManagement/components/PriorityLabel';

const UserIcon = (username?: string) => <View style={[
  styles.profileIcon, username && { backgroundColor: generateAvatarColor(username?.length ?? 5) },
]}>
  {username ? <Text style={styles.name}>
    {getInitials(username)}
  </Text> : <Logo />}
</View>;

export type TicketActivityItem = {
  createdOn: string,
  text: string,
  userEmail?: string,
  UserEmail?: string,
  commentType?: 'RESOLUTION_NOTE' | 'REPORTER_COMMENT',
  eventType?: 'PRIORITY_UPDATED' | 'STATUS_CHANGED' | 'MESSAGE_TO_REPORTER' | 'ASSIGNEE_UPDATED',
  currentPriority?: TicketPriority,
  previousPriority?: TicketPriority,
}

interface TicketActivityListItemProps extends TicketActivityItem {
  users,
}

const TicketActivityListItem = ({
  users, createdOn, text: Title, userEmail, UserEmail, commentType, eventType, currentPriority,
}: TicketActivityListItemProps) => {
  const slaNames: string[] = useSelector((state: {
    selectedBuilding: Building
  }) => state.selectedBuilding.SlaHours
    ?.map((item: BuildingSettingsPriority) => item.Name), shallowEqual);

  if (Title.includes(' changed status')) {
    const initialStatusPhrase: string = Title.split('from')[1].split('to')[0];
    const updatedStatusPhrase: string = Title.split('from')[1].split('to')[1];
    const username: string = Title.split(' changed status')[0];
    let initialStatus: TicketStatus = TicketStatus.REPORTED;
    if (initialStatusPhrase.toLowerCase().includes('paus')) {
      initialStatus = TicketStatus.PAUSED;
    }
    if (initialStatusPhrase.toLowerCase().includes('prog')) {
      initialStatus = TicketStatus.INPROGRESS;
    }
    if (initialStatusPhrase.toLowerCase().includes('compl')) {
      initialStatus = TicketStatus.COMPLETED;
    }
    if (initialStatusPhrase.toLowerCase().includes('open')) {
      initialStatus = TicketStatus.REOPENED;
    }
    let updatedStatus: TicketStatus = TicketStatus.REPORTED;
    if (updatedStatusPhrase.toLowerCase().includes('paus')) {
      updatedStatus = TicketStatus.PAUSED;
    }
    if (updatedStatusPhrase.toLowerCase().includes('prog')) {
      updatedStatus = TicketStatus.INPROGRESS;
    }
    if (updatedStatusPhrase.toLowerCase().includes('compl')) {
      updatedStatus = TicketStatus.COMPLETED;
    }
    if (updatedStatusPhrase.toLowerCase().includes('open')) {
      updatedStatus = TicketStatus.REOPENED;
    }
    return <View style={styles.container}>
      {UserIcon(username)}
      <View style={{ flex: 1 }}>
        <View style={[Style.row, styles.itemWithTimestampFooter]}>
          <Text style={[styles.itemText, styles.statusChangeTitle]}>
            {`${Title.split('from')[0].trim()}:`}
          </Text>
          <Text style={styles.timestamp}>{getNotificationTime(createdOn)}</Text>
        </View>
        <View style={{ flexDirection: 'row', alignItems: 'center', flexWrap: 'wrap' }}>
          <Text style={styles.itemText}>{'From '}</Text>
          <View style={[
            styles.statusChangeItem,
            {
              backgroundColor: getNotificationStatusBackgroundColor(initialStatus),
              borderColor: getNotificationStatusColor(initialStatus),
            },
          ]}>
            <Text style={[
              styles.statusChangeText,
              { color: getNotificationStatusColor(initialStatus) },
            ]}>
              {getNotificationStatusText(initialStatus)}
            </Text>
          </View>
          <Text style={styles.itemText}>{'To '}</Text>
          <View style={[
            styles.statusChangeItem,
            {
              backgroundColor: getNotificationStatusBackgroundColor(updatedStatus),
              borderColor: getNotificationStatusColor(updatedStatus),
            },
          ]}>
            <Text style={[
              styles.statusChangeText,
              { color: getNotificationStatusColor(updatedStatus) },
            ]}>
              {getNotificationStatusText(updatedStatus)}
            </Text>
          </View>
        </View>
      </View>
    </View>;
  }

  if (Title.includes(' has been solved!')) {
    const username: string = Title.split(' assigned to ')[1].trim().split(' has been')[0].trim();
    const updatedStatus: TicketStatus = TicketStatus.COMPLETED;
    return <View style={styles.container}>
      {UserIcon(username)}
      <View style={{ flex: 1 }}>
        <View style={[Style.row, styles.itemWithTimestampFooter]}>
          <Text style={[styles.itemText, styles.statusChangeTitle]} selectable>{Title}</Text>
          <Text style={styles.timestamp} selectable>{getNotificationTime(createdOn)}</Text>
        </View>
        <View style={[
          styles.statusChangeItem,
          {
            backgroundColor: getNotificationStatusBackgroundColor(updatedStatus),
            borderColor: getNotificationStatusColor(updatedStatus),
          },
        ]}>
          <Text style={[
            styles.statusChangeText,
            { color: getNotificationStatusColor(updatedStatus) },
          ]}>
            {getNotificationStatusText(updatedStatus)}
          </Text>
        </View>
      </View>
    </View>;
  }

  let user: { firstName: string, lastName: string } | null = null;
  let username: string | null = null;
  let updatedPriority: TicketPriority = null;
  let updatedAssignee: string | null = null;

  if (eventType === 'PRIORITY_UPDATED' && ticketPriorityIsValid(currentPriority)) {
    updatedPriority = currentPriority;
  }

  if (Title.includes('priority') && Title.includes('changed to')) {
    const updatedPriorityPhrase: string = Title.split('changed to')[1].trim().split(' ')[0]
      ?.toLowerCase();
    if (updatedPriorityPhrase === TicketPriorityTitle.CRITICAL.toLowerCase()
      || updatedPriorityPhrase === slaNames[0].toLowerCase()) {
      updatedPriority = 0;
    }
    if (updatedPriorityPhrase === TicketPriorityTitle.HIGH.toLowerCase()
      || updatedPriorityPhrase === slaNames[1].toLowerCase()) {
      updatedPriority = 1;
    }
    if (updatedPriorityPhrase === TicketPriorityTitle.MODERATE.toLowerCase()
      || updatedPriorityPhrase === slaNames[2].toLowerCase()) {
      updatedPriority = 2;
    }
    if (updatedPriorityPhrase === TicketPriorityTitle.LOW.toLowerCase()
      || updatedPriorityPhrase === slaNames[3].toLowerCase()) {
      updatedPriority = 3;
    }
    if (updatedPriorityPhrase === TicketPriorityTitle.OTHER.toLowerCase()
      || updatedPriorityPhrase === slaNames[4].toLowerCase()) {
      updatedPriority = 4;
    }
  } else if (Title.includes(' has been assigned to ')) {
    // eslint-disable-next-line prefer-destructuring
    updatedAssignee = Title.split('assigned to ')[1].trim().split(' and')[0];
  }

  let formattedTitle: string = Title;
  if (ticketPriorityIsValid(updatedPriority)) {
    formattedTitle = 'The ticket\'s priority has been changed to: ';
  } else if (updatedAssignee) {
    formattedTitle = 'The ticket has been assigned to: ';
  }

  if (userEmail) {
    // eslint-disable-next-line prefer-destructuring
    user = users.filter((u) => u.email === userEmail)[0];
    username = user ? `${user.firstName} ${user.lastName}` : userEmail;
  }

  if (UserEmail) {
    // eslint-disable-next-line prefer-destructuring
    user = users.filter((u) => u.email === UserEmail)[0];
    username = user ? `${user.firstName} ${user.lastName}` : UserEmail;
  }

  const isClosureNote: boolean = commentType === 'RESOLUTION_NOTE';
  const isFormatted: boolean = !!updatedAssignee || ticketPriorityIsValid(updatedPriority)
    || !!username || isClosureNote;

  return <View
    style={[styles.container,
      (username || eventType === 'MESSAGE_TO_REPORTER') && styles.commentItem,
      commentType === 'REPORTER_COMMENT' && { backgroundColor: Colors.light.hereworksBlue50, marginRight: 48 },
      eventType === 'MESSAGE_TO_REPORTER' && { backgroundColor: Colors.light.hereworksGreen50, marginLeft: 48 },
    ]}
    testID={username ? 'placed-commment' : null}
  >
    <View style={[styles.iconContainer, username && styles.commentIconContainer]}>
      {UserIcon(username)}
      {username && <Text style={styles.username}>{username}</Text>}
    </View>
    <View style={[styles.content, isFormatted && styles.itemWithTimestampFooter]}>
      <View style={{ flex: 1 }}>
        {isClosureNote && <Text style={[styles.itemText, styles.closureNoteTitle]}>
          {'Closure Note: '}
        </Text>}
        <View style={styles.iconContainer}>
          <Text style={styles.itemText} selectable>{formattedTitle}</Text>
          {updatedAssignee && <Text style={styles.updatedAssignee} selectable>
            {updatedAssignee}</Text>}
        </View>
        {ticketPriorityIsValid(updatedPriority) && <View style={styles.priorityLabelContainer}>
          <PriorityLabel priority={updatedPriority} />
        </View>}
      </View>
      <Text
        numberOfLines={1}
        style={[styles.timestamp, username && styles.commentTime]}
      >
        {getNotificationTime(createdOn)}
      </Text>
    </View>
  </View>;
};

const styles = StyleSheet.create({
  container: {
    marginVertical: 6,
    flexDirection: 'row',
    alignItems: 'center',
  },
  content: {
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    flex: 1,
  },
  statusChangeItem: {
    marginEnd: 8,
    paddingHorizontal: 8,
    borderRadius: 4,
    borderWidth: 1,
    borderColor: Colors.light.hereworksDarkBlue,
    alignSelf: 'flex-start',
    marginTop: 4,
  },
  statusChangeText: {
    fontSize: 14,
    lineHeight: 22,
    fontFamily: 'Roboto_400Regular',
    color: Colors.light.hereworksDarkBlue,
  },
  statusChangeTitle: {
    marginBottom: 4,
  },
  commentItem: {
    borderRadius: 10,
    backgroundColor: Colors.light.hereworksWhite100,
    borderBottomWidth: 1,
    borderBottomColor: Colors.light.hereworksWhite300,
    paddingHorizontal: 16,
    paddingVertical: 12,
    alignSelf: 'stretch',
    flexDirection: 'column',
    alignItems: 'stretch',
  },
  itemText: {
    fontSize: 14,
    lineHeight: 18,
    fontFamily: 'Roboto_400Regular',
    color: Colors.light.hereworksBlack400,
  },
  updatedAssignee: {
    fontSize: 14,
    lineHeight: 18,
    fontFamily: 'Roboto_500Medium',
    color: Colors.light.hereworksBlue500,
  },
  closureNoteTitle: {
    color: Colors.light.hereworksBlack600,
  },
  timestamp: {
    fontSize: 11,
    lineHeight: 13,
    fontFamily: 'Roboto_400Regular',
    color: Colors.light.hereworksBlack400,
    textAlign: 'right',
    alignSelf: 'flex-start',
  },
  commentTime: {
    alignSelf: 'flex-end',
    marginTop: 0,
  },
  profileIcon: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: Colors.light.hereworksPrimaryGrey,
    marginEnd: 8,
    justifyContent: 'center',
    alignItems: 'center',
  },
  name: {
    fontSize: 14,
    lineHeight: 21,
    fontFamily: 'Roboto_400Regular',
    color: '#FFF',
  },
  username: {
    fontSize: 14,
    lineHeight: 21,
    color: Colors.light.hereworksBlack600,
    fontFamily: 'Poppins_600SemiBold',
  },
  iconContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    flexWrap: 'wrap',
  },
  commentIconContainer: {
    marginBottom: 12,
  },
  itemWithTimestampFooter: {
    flexDirection: 'row',
    flexWrap: 'wrap',
  },
  priorityLabelContainer: {
    alignSelf: 'flex-start',
    marginVertical: 4,
  },
});

export default TicketActivityListItem;
